import type { KpiDefinition, KpiEntryRecord } from "./kpi";

/**
 * Arithmetic over other KPIs — the "Andra nyckeltal" data source.
 *
 * A calculated KPI holds no numbers of its own. It holds an expression over
 * *other* KPIs' values, evaluated at read time against whatever those KPIs
 * resolve to for the requested business, period and subject. Nothing is ever
 * written to `kpiEntries` for a calculated definition, which is what makes
 * "recalculate when the sources change" free: there is no stored copy to go
 * stale, and no way for a human or the nightly autofill to record a competing
 * value.
 *
 * Storage form (`KpiSource.expression`) references KPIs by id, wrapped in
 * `{{…}}`:
 *
 *     {{Ab12Cd34Ef56Gh78Ij90}} / {{Kl12Mn34Op56Qr78St90}}
 *
 * Ids rather than names, so renaming "Bokade möten" never breaks a formula.
 * The editor works in a readable `[Namn]` form instead — see
 * `formatKpiFormulaForEditing` / `parseKpiFormulaFromEditing`, which convert
 * between the two against the current definitions.
 *
 * The evaluator is a hand-written recursive-descent parser over a fixed
 * grammar. Deliberately not `Function`/`eval`: the expression is user input
 * that the server also evaluates, so the only safe evaluator is one that
 * cannot express anything but arithmetic.
 *
 *     expr    := term (("+" | "-") term)*
 *     term    := factor (("*" | "/") factor)*
 *     factor  := ("+" | "-") factor | primary
 *     primary := number | reference | "(" expr ")"
 */

/** Bounds on a stored expression. Generous for real formulas, hostile to abuse. */
export const KPI_FORMULA_MAX_LENGTH = 300;
export const KPI_FORMULA_MAX_REFERENCES = 10;

/**
 * Decimals a calculated result is rounded to. Purely to keep binary-float
 * noise out of stored-looking numbers (0.1 + 0.2 must read as 0.3, and
 * 100000 / 3 must not carry seventeen digits into a chart tooltip). Six is far
 * below any KPI's meaningful precision, so it never changes an answer — unlike
 * rounding for display, which is the UI's business.
 */
export const KPI_CALCULATED_DECIMALS = 6;

// ---------------------------------------------------------------------------
// AST
// ---------------------------------------------------------------------------

export type KpiFormulaOperator = "+" | "-" | "*" | "/";

export type KpiFormulaNode =
  | { kind: "number"; value: number }
  | { kind: "reference"; kpiId: string }
  | { kind: "negate"; operand: KpiFormulaNode }
  | {
      kind: "binary";
      operator: KpiFormulaOperator;
      left: KpiFormulaNode;
      right: KpiFormulaNode;
    };

// ---------------------------------------------------------------------------
// Problems
// ---------------------------------------------------------------------------

/**
 * Why a formula is not usable. Structured rather than pre-formatted: the
 * editor renders these in the user's language, and the server returns the same
 * codes so both surfaces reject for the same stated reason.
 */
export type KpiFormulaIssue =
  | { code: "empty" }
  | { code: "too_long"; max: number }
  | { code: "unexpected_character"; character: string; position: number }
  | { code: "unexpected_token"; position: number }
  | { code: "unbalanced_parenthesis"; position: number }
  | { code: "incomplete_expression" }
  | { code: "no_references" }
  | { code: "too_many_references"; max: number }
  | { code: "self_reference" }
  | { code: "unknown_reference"; kpiId: string }
  | { code: "archived_reference"; kpiId: string }
  | { code: "out_of_scope_reference"; kpiId: string }
  | { code: "calculated_reference_cycle"; kpiIds: string[] }
  /** Editor-only: `[Namn]` that matches no KPI, or more than one. */
  | { code: "unknown_reference_name"; name: string }
  | { code: "ambiguous_reference_name"; name: string };

/**
 * Why a calculated KPI has no value for a given period and subject. An
 * *unavailable* result, never 0 / Infinity / NaN: "we could not work it out"
 * and "the answer is zero" are different facts, and collapsing them would let
 * a missing meeting count read as a collapse in performance.
 */
export type KpiUnavailableReason =
  /** A source KPI has nothing recorded for this period and subject. */
  | { code: "missing_input"; kpiId: string }
  | { code: "divide_by_zero" }
  | { code: "invalid_formula" }
  | { code: "cycle" }
  /** Arithmetic produced a non-finite number (overflow). */
  | { code: "not_finite" };

export type KpiValueResult =
  | { ok: true; value: number }
  | { ok: false; reason: KpiUnavailableReason };

export type KpiFormulaParseResult =
  | { ok: true; node: KpiFormulaNode }
  | { ok: false; issue: KpiFormulaIssue };

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type Token =
  | { type: "number"; value: number; position: number }
  | { type: "reference"; kpiId: string; position: number }
  | { type: "operator"; value: KpiFormulaOperator; position: number }
  | { type: "paren"; value: "(" | ")"; position: number };

/** Firestore auto-ids are alphanumeric; allow the usual id punctuation too. */
const REFERENCE_PATTERN = /^\{\{([A-Za-z0-9_-]{1,128})\}\}/;
const NUMBER_PATTERN = /^\d+(?:[.,]\d+)?/;

function tokenize(
  expression: string,
): { ok: true; tokens: Token[] } | { ok: false; issue: KpiFormulaIssue } {
  const tokens: Token[] = [];
  let i = 0;

  while (i < expression.length) {
    const rest = expression.slice(i);
    const char = expression[i]!;

    if (/\s/.test(char)) {
      i += 1;
      continue;
    }

    if (char === "{") {
      const match = REFERENCE_PATTERN.exec(rest);
      if (!match) {
        return {
          ok: false,
          issue: { code: "unexpected_character", character: char, position: i },
        };
      }
      tokens.push({ type: "reference", kpiId: match[1]!, position: i });
      i += match[0].length;
      continue;
    }

    if (char === "(" || char === ")") {
      tokens.push({ type: "paren", value: char, position: i });
      i += 1;
      continue;
    }

    if (char === "+" || char === "-" || char === "*" || char === "/") {
      tokens.push({ type: "operator", value: char, position: i });
      i += 1;
      continue;
    }

    const numberMatch = NUMBER_PATTERN.exec(rest);
    if (numberMatch) {
      // Swedish keyboards produce a decimal comma; the grammar has no other
      // use for one, so accepting it costs nothing and spares a support ticket.
      tokens.push({
        type: "number",
        value: Number(numberMatch[0].replace(",", ".")),
        position: i,
      });
      i += numberMatch[0].length;
      continue;
    }

    return {
      ok: false,
      issue: { code: "unexpected_character", character: char, position: i },
    };
  }

  return { ok: true, tokens };
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/**
 * Parses a stored expression into an AST. Syntax only — whether the referenced
 * KPIs exist, are in scope, or would form a cycle is `validateKpiFormula`'s
 * job, because answering that needs the whole definition set.
 */
export function parseKpiFormula(expression: string): KpiFormulaParseResult {
  const trimmed = expression.trim();
  if (trimmed === "") return { ok: false, issue: { code: "empty" } };
  if (trimmed.length > KPI_FORMULA_MAX_LENGTH) {
    return {
      ok: false,
      issue: { code: "too_long", max: KPI_FORMULA_MAX_LENGTH },
    };
  }

  const tokenized = tokenize(trimmed);
  if (!tokenized.ok) return { ok: false, issue: tokenized.issue };

  const tokens = tokenized.tokens;
  let cursor = 0;
  let failure: KpiFormulaIssue | null = null;

  function peek(): Token | undefined {
    return tokens[cursor];
  }

  function fail(issue: KpiFormulaIssue): null {
    failure ??= issue;
    return null;
  }

  function parseExpression(): KpiFormulaNode | null {
    let left = parseTerm();
    if (left == null) return null;
    for (;;) {
      const token = peek();
      if (
        token?.type !== "operator" ||
        (token.value !== "+" && token.value !== "-")
      ) {
        return left;
      }
      cursor += 1;
      const right = parseTerm();
      if (right == null) return null;
      left = { kind: "binary", operator: token.value, left, right };
    }
  }

  function parseTerm(): KpiFormulaNode | null {
    let left = parseFactor();
    if (left == null) return null;
    for (;;) {
      const token = peek();
      if (
        token?.type !== "operator" ||
        (token.value !== "*" && token.value !== "/")
      ) {
        return left;
      }
      cursor += 1;
      const right = parseFactor();
      if (right == null) return null;
      left = { kind: "binary", operator: token.value, left, right };
    }
  }

  function parseFactor(): KpiFormulaNode | null {
    const token = peek();
    if (
      token?.type === "operator" &&
      (token.value === "-" || token.value === "+")
    ) {
      cursor += 1;
      const operand = parseFactor();
      if (operand == null) return null;
      return token.value === "-" ? { kind: "negate", operand } : operand;
    }
    return parsePrimary();
  }

  function parsePrimary(): KpiFormulaNode | null {
    const token = peek();
    if (token == null) return fail({ code: "incomplete_expression" });

    if (token.type === "number") {
      cursor += 1;
      return { kind: "number", value: token.value };
    }
    if (token.type === "reference") {
      cursor += 1;
      return { kind: "reference", kpiId: token.kpiId };
    }
    if (token.type === "paren" && token.value === "(") {
      const open = token.position;
      cursor += 1;
      const inner = parseExpression();
      if (inner == null) return null;
      const close = peek();
      if (close?.type !== "paren" || close.value !== ")") {
        return fail({ code: "unbalanced_parenthesis", position: open });
      }
      cursor += 1;
      return inner;
    }
    if (token.type === "paren" && token.value === ")") {
      return fail({ code: "unbalanced_parenthesis", position: token.position });
    }
    return fail({ code: "unexpected_token", position: token.position });
  }

  const node = parseExpression();
  if (node == null) {
    return { ok: false, issue: failure ?? { code: "incomplete_expression" } };
  }
  const leftover = peek();
  if (leftover != null) {
    return {
      ok: false,
      issue: { code: "unexpected_token", position: leftover.position },
    };
  }
  return { ok: true, node };
}

/** Referenced KPI ids in source order, deduplicated. Empty when unparseable. */
export function kpiFormulaReferences(expression: string): string[] {
  const parsed = parseKpiFormula(expression);
  if (!parsed.ok) return [];
  const out: string[] = [];
  const walk = (node: KpiFormulaNode): void => {
    switch (node.kind) {
      case "reference":
        if (!out.includes(node.kpiId)) out.push(node.kpiId);
        return;
      case "negate":
        return walk(node.operand);
      case "binary":
        walk(node.left);
        walk(node.right);
        return;
      default:
        return;
    }
  };
  walk(parsed.node);
  return out;
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

function roundCalculated(value: number): number {
  const factor = 10 ** KPI_CALCULATED_DECIMALS;
  return Math.round(value * factor) / factor;
}

/**
 * Evaluates a parsed formula. `resolve` supplies each referenced KPI's value
 * for the subject and period being asked about, and may itself fail — a source
 * KPI with nothing recorded makes the whole result unavailable rather than
 * contributing a zero.
 */
export function evaluateKpiFormula(
  node: KpiFormulaNode,
  resolve: (kpiId: string) => KpiValueResult,
): KpiValueResult {
  switch (node.kind) {
    case "number":
      return { ok: true, value: node.value };

    case "reference":
      return resolve(node.kpiId);

    case "negate": {
      const operand = evaluateKpiFormula(node.operand, resolve);
      return operand.ok ? { ok: true, value: -operand.value } : operand;
    }

    case "binary": {
      const left = evaluateKpiFormula(node.left, resolve);
      if (!left.ok) return left;
      const right = evaluateKpiFormula(node.right, resolve);
      if (!right.ok) return right;

      let value: number;
      switch (node.operator) {
        case "+":
          value = left.value + right.value;
          break;
        case "-":
          value = left.value - right.value;
          break;
        case "*":
          value = left.value * right.value;
          break;
        case "/":
          // A recorded 0 is a perfectly good value everywhere else; only as a
          // divisor is it an answer we cannot give.
          if (right.value === 0) {
            return { ok: false, reason: { code: "divide_by_zero" } };
          }
          value = left.value / right.value;
          break;
      }
      if (!Number.isFinite(value)) {
        return { ok: false, reason: { code: "not_finite" } };
      }
      return { ok: true, value: roundCalculated(value) };
    }
  }
}

/**
 * One KPI's value for a subject and period, following calculated definitions
 * through to the recorded numbers underneath.
 *
 * `resolveRecorded` answers for KPIs that hold real entries; how it aggregates
 * is the caller's decision, and that is the whole point of the indirection.
 * A per-person cell passes that person's own value; a team total passes the
 * team's aggregate; a chart passes the period total. Because the formula is
 * applied *after* the caller has aggregated, a period's ratio is always
 * computed from period totals, never from a sum or average of narrower ratios.
 *
 * `chain` guards against cycles that slipped past write-time validation (an
 * older document, a partially applied batch) — the value goes unavailable
 * rather than recursing until the stack gives out.
 */
export function resolveKpiValue(
  kpiId: string,
  definitions: Pick<KpiDefinition, "id" | "source">[],
  resolveRecorded: (kpiId: string) => number | null | undefined,
  chain: ReadonlySet<string> = new Set(),
): KpiValueResult {
  const definition = definitions.find((d) => d.id === kpiId);

  if (definition == null || definition.source.kind !== "calculated") {
    const recorded = resolveRecorded(kpiId);
    if (typeof recorded !== "number" || !Number.isFinite(recorded)) {
      return { ok: false, reason: { code: "missing_input", kpiId } };
    }
    return { ok: true, value: recorded };
  }

  if (chain.has(kpiId)) return { ok: false, reason: { code: "cycle" } };

  const parsed = parseKpiFormula(definition.source.expression);
  if (!parsed.ok) return { ok: false, reason: { code: "invalid_formula" } };

  const nextChain = new Set(chain).add(kpiId);
  return evaluateKpiFormula(parsed.node, (referenced) =>
    resolveKpiValue(referenced, definitions, resolveRecorded, nextChain),
  );
}

export interface ResolvedKpiValues {
  /** Recorded values plus every calculated KPI that could be worked out. */
  values: Record<string, number>;
  /** Calculated KPIs that could not be, and why. */
  unavailable: Record<string, KpiUnavailableReason>;
}

/**
 * Adds calculated results to one subject's recorded values for one period —
 * the read-time counterpart to entering a number. Recorded values pass through
 * untouched; a calculated KPI never overwrites one, because a definition is
 * either calculated or recordable, never both.
 */
export function resolveCalculatedKpiValues(
  definitions: Pick<KpiDefinition, "id" | "source" | "archivedAt">[],
  recorded: Record<string, number>,
): ResolvedKpiValues {
  const values: Record<string, number> = { ...recorded };
  const unavailable: Record<string, KpiUnavailableReason> = {};

  for (const definition of definitions) {
    if (definition.source.kind !== "calculated") continue;
    if (definition.archivedAt != null) continue;

    const result = resolveKpiValue(
      definition.id,
      definitions,
      (id) => recorded[id],
    );
    if (result.ok) values[definition.id] = result.value;
    else unavailable[definition.id] = result.reason;
  }

  return { values, unavailable };
}

/**
 * Resolves calculated values on every entry in a set — the single place the
 * read path turns stored documents into what a caller can actually read.
 * Applied per (period, subject), so each entry's formula sees only that
 * subject's own numbers for that period; nothing is mixed across rows.
 *
 * A no-op when the business has no calculated definitions, which is every
 * business that has not opted into them.
 */
/**
 * Whether a team's value for this KPI is the sum of its members rather than a
 * number of its own: an integration KPI attributed per member (GRA-59).
 *
 * The nightly autofill used to write its own team total for these, and the
 * charts prefer a team total over the member sum. A manager's manual member
 * value was protected, but the auto team total ignored it, so a hand-entered
 * 300 000 disappeared behind an auto 0. For these KPIs the team is exactly its
 * members; a team-attributed KPI (pipeline value) keeps its own total.
 */
export function teamValueIsMemberSum(
  def: Pick<KpiDefinition, "source"> | undefined,
): boolean {
  return (
    def != null &&
    def.source.kind === "integration" &&
    def.source.attribution !== "team"
  );
}

/**
 * Entries with the auto-written team totals of member-summed KPIs removed, so
 * every reader falls back to the member sum. A team total someone typed in
 * stays: manual always wins. Read-time only — old documents need no cleanup.
 */
export function withoutAutoMemberSumTeamTotals(
  entries: KpiEntryRecord[],
  definitions: Pick<KpiDefinition, "id" | "source">[],
): KpiEntryRecord[] {
  const memberSummed = new Set(
    definitions.filter(teamValueIsMemberSum).map((d) => d.id),
  );
  if (memberSummed.size === 0) return entries;
  return entries.map((entry) => {
    if (entry.subjectKind !== "team") return entry;
    const drop = Object.keys(entry.values).filter(
      (kpiId) =>
        memberSummed.has(kpiId) && entry.valueSources[kpiId] === "auto",
    );
    if (drop.length === 0) return entry;
    const values = { ...entry.values };
    const valueSources = { ...entry.valueSources };
    for (const kpiId of drop) {
      delete values[kpiId];
      delete valueSources[kpiId];
    }
    return { ...entry, values, valueSources };
  });
}

export function withCalculatedKpiValues(
  entries: KpiEntryRecord[],
  definitions: Pick<KpiDefinition, "id" | "source" | "archivedAt">[],
): KpiEntryRecord[] {
  const hasCalculated = definitions.some(
    (d) => d.archivedAt == null && d.source.kind === "calculated",
  );
  if (!hasCalculated) return entries;

  return entries.map((entry) => {
    const resolved = resolveCalculatedKpiValues(definitions, entry.values);
    return {
      ...entry,
      values: resolved.values,
      unavailable: resolved.unavailable,
    };
  });
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Active calculated KPIs whose formula references `kpiId`. What stands between
 * a user and archiving the KPI three other numbers are built on.
 */
export function kpiFormulaDependents<
  T extends Pick<KpiDefinition, "id" | "source" | "archivedAt">,
>(kpiId: string, definitions: T[]): T[] {
  return definitions.filter(
    (d) =>
      d.archivedAt == null &&
      d.source.kind === "calculated" &&
      kpiFormulaReferences(d.source.expression).includes(kpiId),
  );
}

/**
 * Everything wrong with a formula, in one pass, so the editor can show the
 * first real problem rather than one error per save attempt.
 *
 * `selfKpiId` is the definition being edited (null when creating), and is what
 * makes both self-reference and cycle detection possible: the graph is walked
 * as it would look *after* this save, not as it looks now.
 *
 * References must belong to the same owner scope (`salesTeamId`) as the
 * calculated KPI. `resolveKpisForTeam` gives a team its own definitions if it
 * has any and the org-wide set otherwise — never a mix — so a cross-scope
 * reference would name a KPI that is never recorded alongside this one, and
 * the result would be permanently unavailable. Same scope also means the same
 * Pulse period grid, so source cadences can never disagree.
 */
export function validateKpiFormula(input: {
  expression: string;
  /** Id of the definition being saved; null when creating a new one. */
  selfKpiId: string | null;
  /** Owner scope the calculated KPI will belong to. */
  ownerSalesTeamId: string | null;
  /** Every definition in the business, including archived ones. */
  definitions: Pick<
    KpiDefinition,
    "id" | "source" | "archivedAt" | "salesTeamId"
  >[];
}): KpiFormulaIssue[] {
  const parsed = parseKpiFormula(input.expression);
  if (!parsed.ok) return [parsed.issue];

  const references = kpiFormulaReferences(input.expression);
  if (references.length === 0) return [{ code: "no_references" }];
  if (references.length > KPI_FORMULA_MAX_REFERENCES) {
    return [{ code: "too_many_references", max: KPI_FORMULA_MAX_REFERENCES }];
  }

  const issues: KpiFormulaIssue[] = [];
  for (const kpiId of references) {
    if (input.selfKpiId != null && kpiId === input.selfKpiId) {
      issues.push({ code: "self_reference" });
      continue;
    }
    const referenced = input.definitions.find((d) => d.id === kpiId);
    if (referenced == null) {
      issues.push({ code: "unknown_reference", kpiId });
      continue;
    }
    if (referenced.archivedAt != null) {
      issues.push({ code: "archived_reference", kpiId });
      continue;
    }
    if ((referenced.salesTeamId ?? null) !== input.ownerSalesTeamId) {
      issues.push({ code: "out_of_scope_reference", kpiId });
    }
  }
  if (issues.length > 0) return issues;

  const cycle = findKpiFormulaCycle(
    input.selfKpiId,
    input.expression,
    input.definitions,
  );
  if (cycle != null)
    issues.push({ code: "calculated_reference_cycle", kpiIds: cycle });

  return issues;
}

/**
 * Depth-first walk of the calculated-KPI graph as it would look after saving
 * `expression` for `selfKpiId`, returning the ids on the first cycle found.
 * A pending create (`selfKpiId == null`) cannot close a cycle on its own —
 * nothing references it yet — but its sources may already contain one, and
 * saving a formula onto a broken graph is not an improvement.
 */
function findKpiFormulaCycle(
  selfKpiId: string | null,
  expression: string,
  definitions: Pick<KpiDefinition, "id" | "source">[],
): string[] | null {
  const PENDING = " pending";
  const selfId = selfKpiId ?? PENDING;

  const edges = new Map<string, string[]>();
  for (const definition of definitions) {
    if (definition.id === selfId) continue;
    if (definition.source.kind !== "calculated") continue;
    edges.set(
      definition.id,
      kpiFormulaReferences(definition.source.expression),
    );
  }
  edges.set(selfId, kpiFormulaReferences(expression));

  const visiting = new Set<string>();
  const done = new Set<string>();
  const path: string[] = [];

  function walk(kpiId: string): string[] | null {
    if (done.has(kpiId)) return null;
    if (visiting.has(kpiId)) {
      const start = path.indexOf(kpiId);
      return [...path.slice(start >= 0 ? start : 0), kpiId].filter(
        (id) => id !== PENDING,
      );
    }
    visiting.add(kpiId);
    path.push(kpiId);
    for (const next of edges.get(kpiId) ?? []) {
      const cycle = walk(next);
      if (cycle != null) return cycle;
    }
    path.pop();
    visiting.delete(kpiId);
    done.add(kpiId);
    return null;
  }

  for (const kpiId of edges.keys()) {
    const cycle = walk(kpiId);
    if (cycle != null) return cycle;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Editor syntax
// ---------------------------------------------------------------------------

/**
 * The editor shows `[Bokade möten] / [Försäljningsvärde]` rather than the
 * stored `{{id}}` form: a formula is meant to be read, and nobody reads
 * Firestore ids. The conversion runs against the current definitions on every
 * open, which is exactly why renaming a source KPI leaves the formula intact —
 * only the rendering changes.
 *
 * An id with no matching definition renders as `[?]` so the reference is
 * visibly broken instead of silently vanishing.
 */
export function formatKpiFormulaForEditing(
  expression: string,
  definitions: Pick<KpiDefinition, "id" | "name">[],
): string {
  return expression.replace(
    /\{\{([A-Za-z0-9_-]{1,128})\}\}/g,
    (_match, kpiId: string) => {
      const definition = definitions.find((d) => d.id === kpiId);
      return `[${definition?.name ?? "?"}]`;
    },
  );
}

export type KpiFormulaEditorParseResult =
  | { ok: true; expression: string }
  | { ok: false; issues: KpiFormulaIssue[] };

/**
 * The inverse: `[Namn]` back to `{{id}}`. Names are matched case- and
 * whitespace-insensitively, the same latitude `roleKpiFor` gives them. Two
 * KPIs sharing a name in one scope is ambiguous rather than a coin flip — the
 * user renames one and tries again.
 */
export function parseKpiFormulaFromEditing(
  text: string,
  definitions: Pick<KpiDefinition, "id" | "name">[],
): KpiFormulaEditorParseResult {
  const issues: KpiFormulaIssue[] = [];
  const normalize = (value: string) => value.trim().toLowerCase();

  const expression = text.replace(
    /\[([^\]]*)\]/g,
    (_match, rawName: string) => {
      const wanted = normalize(rawName);
      const matches = definitions.filter((d) => normalize(d.name) === wanted);
      if (matches.length === 0) {
        issues.push({ code: "unknown_reference_name", name: rawName.trim() });
        return "";
      }
      if (matches.length > 1) {
        issues.push({ code: "ambiguous_reference_name", name: rawName.trim() });
        return "";
      }
      return `{{${matches[0]!.id}}}`;
    },
  );

  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, expression: expression.trim() };
}

/** Reference token for a KPI, for the editor's "insert" buttons. */
export function kpiFormulaReferenceToken(kpiId: string): string {
  return `{{${kpiId}}}`;
}
